import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { View, type LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import {
  runOnUI,
  useAnimatedStyle,
  withSpring,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { LearnFlashcardShell } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import { useLearnFlashcardStageTopInsets } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck";
import {
  LEARN_DECK_EXIT_DURATION_MS,
  LEARN_DECK_HEADER_EXIT_CLEARANCE,
  resetLearnDeckMotion,
  useLearnCardHorizontalGesture,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import { LEARN_DECK_RECOVERY_SPRING } from "@-/dev.improve.haniverse.lib.learn.deck";
import { LEARN_SUMMARY_PRESENTATION_KEY } from "#use-learn-summary-card-stage";
import { publishLearnSummaryFlashcardFrame } from "#publish-learn-summary-card-frame";
import { LearnSummaryPresenceContext } from "#learn-summary-presence";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import useLatest from "@-/com.npmjs.use-latest";
import {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";

const ignore = () => {};
export type LearnSummaryFlashcardHandle = { continue: () => void };

/**
 * The welcome card. Start (the CTA, or a committed swipe) opens the deck on the press itself:
 * `onContinue` runs at once, and `LearnSummaryHost` keeps this card above the deck, which is
 * already live underneath, while it flies off. The flight is only a picture; nothing waits for it,
 * for the deck behind it or for data. Which card opens, and its loading, is the deck's business
 * (see the deck host).
 */
export function LearnSummaryFlashcard({
  children,
  onContinue,
  onPendingChange,
  onSwipeBack,
  ref,
  scrollEnabled = false,
  stage,
  touchHandlers,
}: {
  children: ReactNode;
  /** Opens the deck. Returning `false` or rejecting returns the card to rest. */
  onContinue: (() => void | false | Promise<void | false>) | null;
  onPendingChange: (pending: boolean) => void;
  /**
   * A swipe either way presses this instead of Start, and the card springs home: Back to Review
   * while the card shows an exploration, or a CTA that keeps the card (the daily-limit
   * congratulation's Upgrade to Premium).
   */
  onSwipeBack?: () => void;
  ref?: Ref<LearnSummaryFlashcardHandle>;
  scrollEnabled?: boolean;
  /** From `useLearnSummaryFlashcardStage`. */
  stage: LearnDeckStageValues;
  touchHandlers?: Pick<
    import("@-/com.npmjs.react-native").ViewProps,
    "onTouchStart" | "onTouchMove" | "onTouchEnd" | "onTouchCancel"
  >;
}) {
  const { cardTop } = useLearnFlashcardStageTopInsets();
  const [hasSummaryPreview, setSummaryCardHeight] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.hasSummaryPreview,
    (c) => c.setSummaryCardHeight,
  );
  const presence = useContext(LearnSummaryPresenceContext);
  const presenceRef = useLatest(presence);
  const [direction, setDirection] = useState<"left" | "right" | "up" | null>(null);
  const busy = useRef(false);
  const mounted = useRef(true);
  const onContinueRef = useLatest(onContinue);
  const onPendingRef = useLatest(onPendingChange);
  useEffect(() => {
    // The view owns the stage, so it outlives a keyed remount; every card starts at rest.
    runOnUI(resetLearnDeckMotion)(stage, "front", LEARN_SUMMARY_PRESENTATION_KEY, 0);
  }, [stage]);
  useEffect(() => {
    mounted.current = true;
    onPendingRef.current(false);
    return () => {
      mounted.current = false;
    };
  }, [onPendingRef]);
  const returnToRest = useCallback(() => {
    stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
  }, [stage]);
  const recover = useCallback(
    (error: unknown) => {
      if (!mounted.current) {
        return;
      }
      busy.current = false;
      presenceRef.current?.stay();
      setDirection(null);
      onPendingRef.current(false);
      returnToRest();
      stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      stage.exitProgress.set(0);
      stage.exitingOperation.set(null);
      if (error !== null) {
        showErrorToast("Could not open your cards", error);
      }
    },
    [onPendingRef, presenceRef, returnToRest, stage],
  );
  // Purely visual: nothing observes this animation finishing.
  const flyOff = useCallback(
    (nextDirection: "left" | "right" | "up") => {
      const config = { duration: LEARN_DECK_EXIT_DURATION_MS };
      stage.exitProgress.set(withTiming(1, config));
      if (nextDirection === "up") {
        stage.dragX.set(withTiming(0, config));
        stage.tilt.set(withTiming(0, config));
        stage.dragY.set(
          withTiming(-stage.cardHeight.get() - LEARN_DECK_HEADER_EXIT_CLEARANCE, config),
        );
      } else {
        stage.dragX.set(
          withTiming(
            (nextDirection === "left" ? -1 : 1) * (stage.cardWidth.get() + stage.cardHeight.get()),
            config,
          ),
        );
      }
    },
    [stage],
  );
  const start = useCallback(
    (nextDirection: "left" | "right" | "up") => {
      const openDeck = onContinueRef.current;
      if (busy.current) {
        return;
      }
      if (openDeck === null) {
        // Start is unavailable: the card goes home.
        returnToRest();
        stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
        return;
      }
      busy.current = true;
      stage.exitingOperation.set(LEARN_SUMMARY_PRESENTATION_KEY);
      // On the UI thread from the press itself, before React renders anything: opening the deck is
      // the expensive render, and the card flies off while it happens.
      flyOff(nextDirection);
      onPendingRef.current(true);
      setDirection(nextDirection);
      // Before the route changes, so the host keeps this card on top for its flight.
      presenceRef.current?.depart();
      // The async wrapper runs `openDeck` now, and turns a synchronous throw into a rejection.
      void (async () => openDeck())()
        .then((navigated) => {
          if (navigated === false) {
            recover(null);
          }
        })
        .catch(recover);
    },
    [flyOff, onContinueRef, onPendingRef, presenceRef, recover, returnToRest, stage],
  );
  const continueUp = useCallback(() => start("up"), [start]);
  useImperativeHandle(ref, () => ({ continue: continueUp }), [continueUp]);
  useEffect(() => () => setSummaryCardHeight(null), [setSummaryCardHeight]);
  const gesture = useLearnCardHorizontalGesture({
    allowVerticalScroll: scrollEnabled,
    isCommitted: direction !== null,
    isEnabled: (onContinue !== null || onSwipeBack !== undefined) && direction === null,
    onCommit: start,
    onDragCancel: returnToRest,
    onDragStart: ignore,
    onPark: ignore,
    onSwipeBack,
    presentationKey: LEARN_SUMMARY_PRESENTATION_KEY,
    stage,
  });
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: stage.dragX.get() },
      { translateY: stage.dragY.get() },
      { rotateZ: `${stage.tilt.get()}deg` },
    ],
  }));
  const frameRef = useRef<View>(null);
  const handleLayout = useCallback(
    ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
      stage.cardWidth.set(layout.width);
      stage.cardHeight.set(layout.height);
      setSummaryCardHeight(layout.height);
      // The welcome galaxy's entry squares off onto this very frame.
      publishLearnSummaryFlashcardFrame(frameRef.current);
    },
    [setSummaryCardHeight, stage],
  );
  return (
    <View
      className="mx-2.25 mb-2.25 min-h-0 flex-1"
      onLayout={handleLayout}
      ref={frameRef}
      style={{ marginTop: cardTop }}
      {...touchHandlers}
    >
      {/* The deck host previews the next card under this one. Until it does, an empty card stands
          in, so a drag never uncovers bare background. */}
      {hasSummaryPreview ? null : (
        <View
          accessibilityElementsHidden
          className="absolute inset-0"
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          testID="learn-summary-successor"
        >
          <LearnFlashcardShell
            className="absolute inset-0"
            testID="learn-summary-successor-placeholder"
          >
            {null}
          </LearnFlashcardShell>
        </View>
      )}
      <GestureDetector gesture={gesture} touchAction={scrollEnabled ? "pan-y" : "none"}>
        <LearnFlashcardShell
          animatedStyle={animatedStyle}
          className="min-h-0 flex-1 shadow-sm"
          pointerEvents={direction === null ? "auto" : "none"}
          testID="learn-summary-card"
        >
          {children}
        </LearnFlashcardShell>
      </GestureDetector>
    </View>
  );
}
